/**
 * O cadastro do veículo na tela (`R-01`, `R-02`, `R-04`; ADR-041).
 *
 * Três coisas que o servidor já decidiu e esta tela só mostra:
 *
 * - **a ficha da placa** — marca, modelo, cor, categoria, TAG e isenção —, com a fonte escrita
 *   ao lado. Hoje a fonte é "base simulada", e a tela diz isso toda vez;
 * - **a categoria contra o cadastro**: confere, diverge (e o porquê provável), ou o cadastro
 *   preencheu o que a IA não leu;
 * - **o status CDT que as regras sugerem** — isento, pago pela TAG ou pagamento público —,
 *   com o motivo.
 *
 * Placa que o cadastro não tem é "não encontrada", nunca uma ficha em branco com cara de
 * dado.
 */
import type { ReactNode } from "react";

import { APRESENTACAO_DO_STATUS } from "../api/tipos";
import type { DetalheDoTransito } from "../api/tipos";
import { Badge, Cartao, Linha } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { hora } from "../formato";
import {
  ROTULO_DA_TAG,
  comparacaoComOCadastro,
  nomeDoVeiculo,
  tagDaFicha,
} from "./cadastro";

/** A linha compacta da conferência, logo abaixo da categoria. */
export function CadastroNaConferencia({
  detalhe,
  categoriaEscolhida,
}: {
  detalhe: DetalheDoTransito;
  categoriaEscolhida: number | null;
}) {
  if (!detalhe.placa || !detalhe.cadastro_consultado_em) return null;
  const v = detalhe.veiculo;
  const conferida = detalhe.categoria_no_cadastro;
  const comparacao = comparacaoComOCadastro(categoriaEscolhida, v?.categoria);
  return (
    <section
      aria-label="Cadastro do veículo"
      className="grid gap-1 rounded-cartao border border-linha bg-painel-2 px-2.5 py-2 text-[11px] text-tinta-3"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <b className="text-titulo-cartao font-bold tracking-[0.07em] uppercase">
          Cadastro do veículo
        </b>
        <Badge cor="cinza">{v?.fonte ?? "base simulada"}</Badge>
        {comparacao && <Badge cor={comparacao.cor}>{comparacao.texto}</Badge>}
      </div>
      {v ? (
        <>
          <span className="text-tinta-2">
            {nomeDoVeiculo(v)}
            {v.categoria != null && ` · categoria ${v.categoria}`}
            {" · "}
            {v.status_tag === "NENHUMA" ? "sem TAG" : `TAG ${tagDaFicha(v)}`}
            {v.isencao && ` · isento: ${v.isencao}`}
          </span>
          {conferida?.situacao === "DIVERGENTE" &&
            categoriaEscolhida === conferida.lida && (
              <span className="text-amarelo">{conferida.explicacao}</span>
            )}
        </>
      ) : (
        <span>Placa {detalhe.placa} não encontrada no cadastro.</span>
      )}
    </section>
  );
}

/** Os dois cartões do detalhe da SENATRAN: o cadastro e o status CDT sugerido. */
export function CartoesDoCadastro({ detalhe }: { detalhe: DetalheDoTransito }) {
  const v = detalhe.veiculo;
  const conferida = detalhe.categoria_no_cadastro;
  const sugestao = detalhe.status_cdt_sugerido;
  // Só os três desfechos que a regra sugere; cancelado e não enviável são outra história.
  const decidido = ["ISENTO", "PAGO", "PENDENTE_PAGAMENTO"].includes(
    detalhe.status,
  );
  let cartaoDoCadastro: ReactNode = null;
  if (detalhe.placa && detalhe.cadastro_consultado_em) {
    cartaoDoCadastro = (
      <Cartao titulo="Cadastro do veículo" icone={<Icone.Lista />}>
        {v ? (
          <>
            <Linha rotulo="Veículo">{nomeDoVeiculo(v)}</Linha>
            <Linha rotulo="Categoria" mono>
              {v.categoria ?? "—"}
              {conferida && conferida.situacao !== "SEM_DADOS" && (
                <span className="ml-1.5">
                  <Badge
                    cor={
                      conferida.situacao === "CORRESPONDE"
                        ? "verde"
                        : conferida.situacao === "DIVERGENTE"
                          ? "amarelo"
                          : "cinza"
                    }
                  >
                    {conferida.situacao === "CORRESPONDE"
                      ? "confere"
                      : conferida.situacao === "DIVERGENTE"
                        ? "diverge"
                        : "sem leitura"}
                  </Badge>
                </span>
              )}
            </Linha>
            <Linha rotulo="TAG">{tagDaFicha(v)}</Linha>
            {v.isencao && <Linha rotulo="Isenção">{v.isencao}</Linha>}
            {conferida?.situacao === "DIVERGENTE" && (
              <p className="text-[10.5px] text-amarelo">
                {conferida.explicacao}
              </p>
            )}
          </>
        ) : (
          <p className="text-[11px] text-tinta-3">
            Placa {detalhe.placa} não encontrada no cadastro.
          </p>
        )}
        <p className="text-[10px] text-tinta-3">
          {v?.fonte ?? "base simulada"} · consultada às{" "}
          {hora(detalhe.cadastro_consultado_em)}
        </p>
      </Cartao>
    );
  }
  return (
    <>
      {cartaoDoCadastro}
      {sugestao && (
        <Cartao titulo="Status CDT sugerido" icone={<Icone.Alerta />}>
          <Linha rotulo="Sugestão">
            <Badge cor={APRESENTACAO_DO_STATUS[sugestao.status].cor}>
              {APRESENTACAO_DO_STATUS[sugestao.status].rotulo}
            </Badge>
          </Linha>
          <Linha rotulo="TAG">
            {ROTULO_DA_TAG[sugestao.tag]}
            {sugestao.origem_da_tag &&
              ` · ${sugestao.origem_da_tag === "portico" ? "lida no pórtico" : "do cadastro"}`}
          </Linha>
          <p className="text-[10.5px] text-tinta-2">{sugestao.motivo}</p>
          <LinhaDaIsencao detalhe={detalhe} />
          {decidido && detalhe.status !== sugestao.status && (
            <p className="text-[10.5px] text-amarelo">
              O desfecho informado (
              {APRESENTACAO_DO_STATUS[detalhe.status].rotulo.toLowerCase()}) não
              é o que a regra sugere.
            </p>
          )}
        </Cartao>
      )}
    </>
  );
}

const ORIGEM_DA_ISENCAO = {
  LISTA: "na lista de isentos",
  CADASTRO: "no cadastro do veículo",
  CARACTERIZACAO: "pela caracterização do veículo",
} as const;

/**
 * De onde veio a isenção (`A-06`, ADR-065): a lista de isentos, o cadastro ou a
 * caracterização — nessa ordem —, com o fundamento quando é a lista e a fonte dita: a lista
 * é mantida no cockpit e não é base oficial.
 */
function LinhaDaIsencao({ detalhe }: { detalhe: DetalheDoTransito }) {
  const isencao = detalhe.isencao;
  if (!isencao || isencao.resultado !== "ISENTO" || !isencao.origem)
    return null;
  return (
    <div className="grid gap-0.5 text-[10.5px]">
      <span className="text-tinta-2">
        Isento {ORIGEM_DA_ISENCAO[isencao.origem]}
        {isencao.lista && `: ${isencao.lista.fundamento}`}
      </span>
      {isencao.lista && (
        <span className="text-tinta-3">
          {isencao.lista.fonte}
          {isencao.consultada_em &&
            ` · consultada às ${hora(isencao.consultada_em)}`}
        </span>
      )}
      {isencao.aviso && <span className="text-amarelo">{isencao.aviso}</span>}
    </div>
  );
}
